import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { 
  Film, ArrowLeft, User, Mail, Phone, Building2, Camera, 
  Save, Loader2, LogOut
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const Profile = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    company: user?.company || ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    // API call would go here
    setTimeout(() => {
      setSaving(false);
      setEditing(false);
    }, 1000);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getRoleLabel = (role) => {
    const labels = {
      super_admin: 'Super Admin',
      casting_manager: 'Casting Manager',
      client: 'Director / Producer / Agency',
      talent: 'Talent / Actor / Model'
    };
    return labels[role] || role;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 nav-glass">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/dashboard')} 
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Dashboard</span>
            </button>

            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary flex items-center justify-center">
                <Film className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-display text-xl tracking-wide">CASTDECK</span>
            </Link>

            <Button 
              onClick={handleLogout}
              variant="outline" 
              className="border-white/10"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Profile Settings</h1>
          <p className="text-muted-foreground">Manage your account information</p>
        </div>

        <div className="bg-card border border-border p-8">
          {/* Avatar Section */}
          <div className="flex items-center gap-6 mb-8 pb-8 border-b border-border">
            <div className="relative">
              {user?.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name} 
                  className="profile-avatar"
                />
              ) : (
                <div className="w-[120px] h-[120px] rounded-full bg-primary/10 flex items-center justify-center border-3 border-primary">
                  <span className="text-4xl font-bold text-primary">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <button className="absolute bottom-0 right-0 w-10 h-10 bg-primary flex items-center justify-center rounded-full">
                <Camera className="w-5 h-5 text-primary-foreground" />
              </button>
            </div>
            <div>
              <h2 className="text-2xl font-bold" data-testid="user-name">{user?.name}</h2>
              <p className="text-muted-foreground">{user?.email}</p>
              <span className="inline-block mt-2 px-3 py-1 text-xs font-bold uppercase bg-primary/10 text-primary border border-primary/20">
                {getRoleLabel(user?.role)}
              </span>
            </div>
          </div>

          {/* Form */}
          <div className="space-y-6">
            <div>
              <Label className="text-sm font-medium mb-2 block">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  disabled={!editing}
                  className="pl-10 h-12 bg-input/20 border-white/10 disabled:opacity-70"
                  data-testid="name-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium mb-2 block">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  value={user?.email || ''}
                  disabled
                  className="pl-10 h-12 bg-input/20 border-white/10 opacity-70"
                  data-testid="email-input"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
            </div>

            <div>
              <Label className="text-sm font-medium mb-2 block">Phone Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  disabled={!editing}
                  placeholder="+91..."
                  className="pl-10 h-12 bg-input/20 border-white/10 disabled:opacity-70"
                  data-testid="phone-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium mb-2 block">Company / Organization</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  value={formData.company}
                  onChange={(e) => handleChange('company', e.target.value)}
                  disabled={!editing}
                  placeholder="Your company name"
                  className="pl-10 h-12 bg-input/20 border-white/10 disabled:opacity-70"
                  data-testid="company-input"
                />
              </div>
            </div>

            <div className="flex items-center gap-4 pt-6 border-t border-border">
              {editing ? (
                <>
                  <Button 
                    onClick={handleSave} 
                    className="btn-gold"
                    disabled={saving}
                    data-testid="save-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 spinner mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                  <Button 
                    onClick={() => setEditing(false)} 
                    variant="outline" 
                    className="border-white/10"
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button 
                  onClick={() => setEditing(true)} 
                  variant="outline" 
                  className="border-white/10"
                  data-testid="edit-btn"
                >
                  Edit Profile
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Account Info */}
        <div className="mt-8 bg-card border border-border p-6">
          <h3 className="font-semibold mb-4">Account Information</h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">User ID</span>
              <p className="font-mono text-xs mt-1">{user?.user_id}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Account Created</span>
              <p className="mt-1">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : '-'}
              </p>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="mt-8 bg-red-500/5 border border-red-500/20 p-6">
          <h3 className="font-semibold text-red-400 mb-4">Danger Zone</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Once you delete your account, there is no going back. Please be certain.
          </p>
          <Button variant="destructive" className="bg-red-600 hover:bg-red-700" data-testid="delete-account-btn">
            Delete Account
          </Button>
        </div>
      </main>
    </div>
  );
};

export default Profile;
